import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '../../components/layout/AppLayout'
import { Chip } from '../../components/ui/Chip'
import { Button } from '../../components/ui/Button'
import { apiFetch, ApiError } from '../../lib/api-client'
import { KontakterTab, OpgaverTab, DokumenterTab, BillederTab } from '../../components/surfaces/SurfaceTabs'
import { CustomFieldsPanel } from '../../components/surfaces/CustomFieldsPanel'

export interface PropertyDetail {
  id: string
  tenant_id: string
  company_id: string
  address: string
  name: string | null
  postnr: string | null
  postnrnavn: string | null
  commissioning_date: string | null
  consumption_types: string[]
  serial_number: number | null
  serial_label: string
  bbr_id: string | null
  area_m2: number | null
  year_built: number | null
  status: string
  lease_count: number
  notes: string | null
  reg_omkostningsbestemt: boolean
  reg_beboerrepraesentation: boolean
}

// Lejeregime udledt af ibrugtagningsdato + omkostningsbestemt-flag. Ejendomme
// ibrugtaget efter 31/12 1991 er undtaget omkostningsbestemt leje (fri leje).
// Visning kun — selve §-clause-gatingen i kontrakten er advokat-gated.
function lejeregime(p: { commissioning_date: string | null; reg_omkostningsbestemt: boolean }): string {
  const year = p.commissioning_date ? parseInt(p.commissioning_date.slice(0, 4), 10) : null
  if (year && year > 1991) return 'Fri leje (ibrugtaget efter 1991)'
  if (p.reg_omkostningsbestemt) return 'Omkostningsbestemt leje'
  return 'Markedsleje / ikke fastlagt'
}

export interface LeaseItem {
  id: string
  property_id: string
  unit_no: string
  type: string
  area_m2: number | null
  room_count: number | null
  serial_number: number | null
}

type Tab = 'generelt' | 'lejemaal' | 'faellesarealer' | 'forbrug' | 'kontokort' | 'billeder' | 'dokumenter' | 'kontakter' | 'opgaver' | 'opsaetning'

const TABS: { key: Tab; label: string }[] = [
  { key: 'generelt', label: 'Generelt' },
  { key: 'lejemaal', label: 'Lejemål' },
  { key: 'faellesarealer', label: 'Fællesarealer' },
  { key: 'forbrug', label: 'Forbrug' },
  { key: 'kontokort', label: 'Kontokort' },
  { key: 'billeder', label: 'Billeder' },
  { key: 'dokumenter', label: 'Dokumenter' },
  { key: 'kontakter', label: 'Kontakter' },
  { key: 'opgaver', label: 'Opgaver' },
  { key: 'opsaetning', label: 'Opsætning' },
]

function statusVariant(s: string): 'green' | 'amber' | 'gray' | 'red' {
  return s === 'active' ? 'green' : s === 'pending' ? 'amber' : 'gray'
}

function statusLabel(s: string): string {
  return s === 'active' ? 'Aktiv' : s === 'inactive' ? 'Inaktiv' : s
}

// ─── Generelt Tab ────────────────────────────────────────────────────────────

const CONSUMPTION_LABELS: Record<string, string> = {
  drift: 'Drift', el: 'El', faellesudgifter: 'Fællesudgifter',
  koeling: 'Køling', ovrig_forbrug: 'Øvrig forbrug',
  vand: 'Vand', vandafledning: 'Vandafledning', varme: 'Varme',
}

function StamdataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '10px 24px', fontSize: 13, color: '#6b7280', width: 200, whiteSpace: 'nowrap' }}>
        {label}
      </td>
      <td style={{ padding: '10px 24px', fontSize: 13, color: '#111827', fontWeight: 500 }}>
        {value ?? <span style={{ color: '#d1d5db' }}>—</span>}
      </td>
    </tr>
  )
}

interface PropertyStats {
  lease_count: number
  udlejet: number
  ledig: number
  samlet_maanedsleje_oere: number
  samlet_depositum_oere: number
}

function GenereltTab({ property }: { property: PropertyDetail }) {
  const [notes, setNotes] = useState(property.notes ?? '')
  const [savedNotes, setSavedNotes] = useState(property.notes ?? '')
  const [stats, setStats] = useState<PropertyStats | null>(null)
  useEffect(() => {
    apiFetch<PropertyStats>(`/api/v1/properties/${property.id}/stats`).then(setStats).catch(() => {})
  }, [property.id])
  const kr = (oere: number) => (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 0 }) + ' kr.'
  const saveNotes = async () => {
    if (notes === savedNotes) return
    try {
      await apiFetch(`/api/v1/properties/${property.id}`, { method: 'PATCH', body: JSON.stringify({ notes }) })
      setSavedNotes(notes)
    } catch { /* surfaced by global handler */ }
  }
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
      <div>
        {/* Stamdata */}
        <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginBottom: 20 }}>
          <div style={{
            padding: '14px 24px',
            borderBottom: '1px solid #e8eaed',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Stamdata
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <StamdataRow label="Status" value={<Chip variant={statusVariant(property.status)}>{statusLabel(property.status)}</Chip>} />
              <StamdataRow label="Løbenummer" value={
                <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{property.serial_label}</span>
              } />
              <StamdataRow label="Navn" value={property.name} />
              <StamdataRow label="Adresse" value={property.address} />
              <StamdataRow label="Postnr. og by" value={
                property.postnr && property.postnrnavn
                  ? `${property.postnr} ${property.postnrnavn}`
                  : null
              } />
              <StamdataRow label="Ibrugtagningsdato" value={property.commissioning_date} />
              <StamdataRow label="Lejeregime" value={lejeregime(property)} />
              <StamdataRow label="Beboerrepræsentation" value={property.reg_beboerrepraesentation ? 'Ja' : 'Nej'} />
              <StamdataRow label="Forbrugstyper" value={
                property.consumption_types.length > 0
                  ? property.consumption_types.map(k => CONSUMPTION_LABELS[k] ?? k).join(', ')
                  : null
              } />
              <StamdataRow label="BBR-ID" value={property.bbr_id} />
              <StamdataRow label="Opførselsår" value={property.year_built} />
              <StamdataRow label="Bebygget areal" value={property.area_m2 != null ? `${property.area_m2} m²` : null} />
            </tbody>
          </table>
        </div>

        {/* Nøgletal */}
        <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
          <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Nøgletal
            </span>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              <StamdataRow label="Antal lejemål" value={`${stats?.lease_count ?? property.lease_count} lejemål`} />
              <StamdataRow label="Udlejet / ledig" value={stats ? `${stats.udlejet} udlejet · ${stats.ledig} ledig` : null} />
              <StamdataRow label="Samlet månedsleje" value={stats ? kr(stats.samlet_maanedsleje_oere) : null} />
              <StamdataRow label="Samlet depositum" value={stats ? kr(stats.samlet_depositum_oere) : null} />
            </tbody>
          </table>
        </div>

        <CustomFieldsPanel basePath={`/api/v1/properties/${property.id}`} />
      </div>

      {/* Notat sidebar */}
      <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10 }}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Notat
          </span>
        </div>
        <div style={{ padding: 16 }}>
          <textarea
            placeholder="Notater kan anføres her..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={saveNotes}
            style={{
              width: '100%',
              minHeight: 120,
              border: '1px solid #e8eaed',
              borderRadius: 6,
              padding: 10,
              fontSize: 13,
              color: '#111827',
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>
    </div>
  )
}

// ─── Lejemål Tab ─────────────────────────────────────────────────────────────

function LejemaalTab({ property }: { property: PropertyDetail }) {
  const navigate = useNavigate()
  const [leases, setLeases] = useState<LeaseItem[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    apiFetch<LeaseItem[]>(`/api/v1/leases?property_id=${property.id}`)
      .then(setLeases)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [property.id])

  const typeLabel = (t: string) => {
    const map: Record<string, string> = {
      residential: 'Bolig', commercial: 'Erhverv',
      garage: 'Garage', storage: 'Depot',
    }
    return map[t] ?? t
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 24px',
        borderBottom: '1px solid #e8eaed',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Lejemål ({leases.length})
        </span>
        <Button size="sm" onClick={() => navigate(`/lejemaal/opret?property_id=${property.id}`)}>
          + Opret lejemål
        </Button>
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>
      )}

      {!loading && leases.length === 0 && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
            Ingen lejemål under denne ejendom endnu.
          </p>
          <Button size="sm" onClick={() => navigate(`/lejemaal/opret?property_id=${property.id}`)}>
            + Opret lejemål
          </Button>
        </div>
      )}

      {!loading && leases.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Løbenummer', 'Adresse / Enhed', 'Type', 'Areal'].map(h => (
                <th key={h} style={{
                  padding: '9px 24px',
                  textAlign: 'left',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e8eaed',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leases.map((l, i) => {
              const unitSerial = l.serial_number != null
                ? String(l.serial_number).padStart(2, '0')
                : '—'
              const fullLabel = `${property.serial_label}-${unitSerial}`
              return (
                <tr
                  key={l.id}
                  onClick={() => navigate(`/lejemaal/${l.id}`)}
                  style={{ borderBottom: i < leases.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer' }}
                >
                  <td style={{ padding: '12px 24px' }}>
                    <span style={{
                      fontFamily: 'monospace', fontSize: 12, fontWeight: 600,
                      color: '#4f46e5', background: '#eff6ff',
                      padding: '2px 6px', borderRadius: 4,
                    }}>
                      {fullLabel}
                    </span>
                  </td>
                  <td style={{ padding: '12px 24px', color: '#111827', fontWeight: 500 }}>
                    {l.unit_no}
                  </td>
                  <td style={{ padding: '12px 24px', color: '#6b7280' }}>
                    {typeLabel(l.type)}
                  </td>
                  <td style={{ padding: '12px 24px', color: '#6b7280' }}>
                    {l.area_m2 != null ? `${l.area_m2} m²` : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

// ─── Forbrug Tab — Forbrugsgrupper ───────────────────────────────────────────

type Forbrugstype = 'el' | 'vand' | 'varme' | 'koeling'

interface Forbrugsgruppe {
  id: string
  tenant_id: string
  property_id: string
  forbrugstype: string
  navn: string
  start_maaned: number | null
  member_count: number
}

const FORBRUGSTYPE_OPTIONS: { value: Forbrugstype; label: string }[] = [
  { value: 'el', label: 'El' },
  { value: 'vand', label: 'Vand' },
  { value: 'varme', label: 'Varme' },
  { value: 'koeling', label: 'Køling' },
]
const FORBRUGSTYPE_LABELS: Record<string, string> = {
  el: 'El', vand: 'Vand', varme: 'Varme', koeling: 'Køling',
}
const MAANED_LABELS = ['', 'Januar', 'Februar', 'Marts', 'April', 'Maj', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'December']

const modalInputStyle: React.CSSProperties = {
  width: '100%', height: 38, padding: '0 12px', borderRadius: 7,
  border: '1px solid #e8eaed', fontSize: 14, color: '#111827',
  background: '#fff', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
}
const fieldLabelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: 6,
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460,
          maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e8eaed', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{title}</span>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 20, lineHeight: 1, padding: 0 }}
          >×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  )
}

function CreateGroupModal({ propertyId, onClose, onCreated }: {
  propertyId: string; onClose: () => void; onCreated: () => void
}) {
  const [forbrugstype, setForbrugstype] = useState<Forbrugstype>('el')
  const [navn, setNavn] = useState('')
  const [startMaaned, setStartMaaned] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCreate = async () => {
    if (!navn.trim()) { setError('Angiv et navn.'); return }
    setSaving(true)
    setError(null)
    try {
      await apiFetch(`/api/v1/properties/${propertyId}/forbrugsgrupper`, {
        method: 'POST',
        body: JSON.stringify({
          forbrugstype,
          navn: navn.trim(),
          start_maaned: startMaaned ? parseInt(startMaaned) : null,
        }),
      })
      onCreated()
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kunne ikke oprette forbrugsgruppe.')
      setSaving(false)
    }
  }

  return (
    <Modal title="Opret forbrugsgruppe" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>Forbrugstype</label>
        <select value={forbrugstype} onChange={e => setForbrugstype(e.target.value as Forbrugstype)} style={modalInputStyle}>
          {FORBRUGSTYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>Navn</label>
        <input value={navn} onChange={e => setNavn(e.target.value)} placeholder="F.eks. Fjernvarme opgang A" style={modalInputStyle} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={fieldLabelStyle}>Regnskabs-startmåned (valgfri)</label>
        <select value={startMaaned} onChange={e => setStartMaaned(e.target.value)} style={modalInputStyle}>
          <option value="">Ingen</option>
          {MAANED_LABELS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
      </div>
      {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 0, marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Annuller</Button>
        <Button onClick={handleCreate} isLoading={saving}>Opret</Button>
      </div>
    </Modal>
  )
}

function EditGroupModal({ group, onClose, onSaved }: {
  group: Forbrugsgruppe; onClose: () => void; onSaved: () => void
}) {
  const [navn, setNavn] = useState(group.navn)
  const [startMaaned, setStartMaaned] = useState(group.start_maaned != null ? String(group.start_maaned) : '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    if (!navn.trim()) { setError('Angiv et navn.'); return }
    setSaving(true)
    setError(null)
    try {
      await apiFetch(`/api/v1/forbrugsgrupper/${group.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          navn: navn.trim(),
          start_maaned: startMaaned ? parseInt(startMaaned) : null,
        }),
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kunne ikke gemme ændringer.')
      setSaving(false)
    }
  }

  return (
    <Modal title="Rediger forbrugsgruppe" onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>Forbrugstype</label>
        <input value={FORBRUGSTYPE_LABELS[group.forbrugstype] ?? group.forbrugstype} disabled style={{ ...modalInputStyle, background: '#f9fafb', color: '#9ca3af' }} />
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={fieldLabelStyle}>Navn</label>
        <input value={navn} onChange={e => setNavn(e.target.value)} style={modalInputStyle} />
      </div>
      <div style={{ marginBottom: 20 }}>
        <label style={fieldLabelStyle}>Regnskabs-startmåned (valgfri)</label>
        <select value={startMaaned} onChange={e => setStartMaaned(e.target.value)} style={modalInputStyle}>
          <option value="">Ingen</option>
          {MAANED_LABELS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
        </select>
      </div>
      {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 0, marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Annuller</Button>
        <Button onClick={handleSave} isLoading={saving}>Gem</Button>
      </div>
    </Modal>
  )
}

function MembershipModal({ group, leases, onClose, onSaved }: {
  group: Forbrugsgruppe; leases: LeaseItem[]; onClose: () => void; onSaved: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    apiFetch<{ lease_ids: string[] }>(`/api/v1/forbrugsgrupper/${group.id}/lejemaal`)
      .then(r => setSelected(new Set(r.lease_ids)))
      .catch(() => setError('Kunne ikke hente medlemmer.'))
      .finally(() => setLoading(false))
  }, [group.id])

  const toggle = (leaseId: string) => setSelected(prev => {
    const n = new Set(prev)
    if (n.has(leaseId)) n.delete(leaseId); else n.add(leaseId)
    return n
  })

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await apiFetch(`/api/v1/forbrugsgrupper/${group.id}/lejemaal`, {
        method: 'PUT',
        body: JSON.stringify({ lease_ids: Array.from(selected) }),
      })
      onSaved()
      onClose()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Kunne ikke gemme medlemmer.')
      setSaving(false)
    }
  }

  return (
    <Modal title={`Lejemål i «${group.navn}»`} onClose={onClose}>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
        Vælg de lejemål, der er tilkoblet denne forbrugsgruppe ({FORBRUGSTYPE_LABELS[group.forbrugstype] ?? group.forbrugstype}).
      </p>
      {loading && <div style={{ padding: 24, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>}
      {!loading && leases.length === 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af' }}>Ingen lejemål under denne ejendom.</p>
      )}
      {!loading && leases.length > 0 && (
        <div style={{ border: '1px solid #e8eaed', borderRadius: 8, maxHeight: 280, overflowY: 'auto', marginBottom: 16 }}>
          {leases.map((l, i) => (
            <label
              key={l.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderBottom: i < leases.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer',
              }}
            >
              <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggle(l.id)} />
              <span style={{ fontSize: 13, color: '#111827', fontWeight: 500 }}>{l.unit_no}</span>
              <span style={{ fontSize: 12, color: '#9ca3af' }}>
                {l.area_m2 != null ? `${l.area_m2} m²` : ''}
              </span>
            </label>
          ))}
        </div>
      )}
      {error && <p style={{ fontSize: 12, color: '#dc2626', marginTop: 0, marginBottom: 12 }}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button variant="secondary" onClick={onClose}>Annuller</Button>
        <Button onClick={handleSave} isLoading={saving} disabled={loading}>Gem ({selected.size})</Button>
      </div>
    </Modal>
  )
}

function ForbrugTab({ property }: { property: PropertyDetail }) {
  const [groups, setGroups] = useState<Forbrugsgruppe[]>([])
  const [leases, setLeases] = useState<LeaseItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [editGroup, setEditGroup] = useState<Forbrugsgruppe | null>(null)
  const [memberGroup, setMemberGroup] = useState<Forbrugsgruppe | null>(null)

  const loadGroups = () => {
    apiFetch<Forbrugsgruppe[]>(`/api/v1/properties/${property.id}/forbrugsgrupper`)
      .then(setGroups)
      .catch(() => setError('Kunne ikke hente forbrugsgrupper.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadGroups()
    apiFetch<LeaseItem[]>(`/api/v1/leases?property_id=${property.id}`)
      .then(setLeases)
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [property.id])

  const handleDelete = async (group: Forbrugsgruppe) => {
    if (!window.confirm(`Slet forbrugsgruppen «${group.navn}»? Tilkoblingen til lejemål fjernes også.`)) return
    try {
      await apiFetch(`/api/v1/forbrugsgrupper/${group.id}`, { method: 'DELETE' })
      loadGroups()
    } catch {
      alert('Kunne ikke slette forbrugsgruppen.')
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{
        padding: '14px 24px', borderBottom: '1px solid #e8eaed',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Forbrugsgrupper ({groups.length})
        </span>
        <Button size="sm" onClick={() => setShowCreate(true)}>+ Opret forbrugsgruppe</Button>
      </div>

      {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: '#9ca3af' }}>Henter…</div>}
      {!loading && error && <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: '#dc2626' }}>{error}</div>}

      {!loading && !error && groups.length === 0 && (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
            Ingen forbrugsgrupper på denne ejendom endnu. Opret en gruppe for at tilkoble lejemål til el, vand, varme eller køling.
          </p>
          <Button size="sm" onClick={() => setShowCreate(true)}>+ Opret forbrugsgruppe</Button>
        </div>
      )}

      {!loading && !error && groups.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f9fafb' }}>
              {['Forbrugstype', 'Navn', 'Lejemål', 'Måned', 'Integration', ''].map((h, i) => (
                <th key={i} style={{
                  padding: '9px 24px', textAlign: i === 5 ? 'right' : 'left',
                  fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase',
                  letterSpacing: '0.05em', borderBottom: '1px solid #e8eaed',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g, i) => (
              <tr key={g.id} style={{ borderBottom: i < groups.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <td style={{ padding: '12px 24px' }}>
                  <span style={{
                    fontSize: 12, fontWeight: 600, color: '#4f46e5',
                    background: '#eff6ff', padding: '2px 8px', borderRadius: 4,
                  }}>
                    {FORBRUGSTYPE_LABELS[g.forbrugstype] ?? g.forbrugstype}
                  </span>
                </td>
                <td style={{ padding: '12px 24px', color: '#111827', fontWeight: 500 }}>{g.navn}</td>
                <td style={{ padding: '12px 24px', color: '#6b7280' }}>
                  <button
                    type="button" onClick={() => setMemberGroup(g)}
                    style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 13, padding: 0, fontFamily: 'inherit' }}
                  >
                    {g.member_count} lejemål
                  </button>
                </td>
                <td style={{ padding: '12px 24px', color: '#6b7280' }}>
                  {g.start_maaned != null ? MAANED_LABELS[g.start_maaned] : <span style={{ color: '#d1d5db' }}>—</span>}
                </td>
                <td style={{ padding: '12px 24px', color: '#9ca3af' }}>Manuel</td>
                <td style={{ padding: '12px 24px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button
                    type="button" onClick={() => setMemberGroup(g)}
                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, padding: '0 8px', fontFamily: 'inherit' }}
                  >Lejemål</button>
                  <button
                    type="button" onClick={() => setEditGroup(g)}
                    style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: 12, padding: '0 8px', fontFamily: 'inherit' }}
                  >Rediger</button>
                  <button
                    type="button" onClick={() => handleDelete(g)}
                    style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12, padding: '0 8px', fontFamily: 'inherit' }}
                  >Slet</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showCreate && (
        <CreateGroupModal propertyId={property.id} onClose={() => setShowCreate(false)} onCreated={loadGroups} />
      )}
      {editGroup && (
        <EditGroupModal group={editGroup} onClose={() => setEditGroup(null)} onSaved={loadGroups} />
      )}
      {memberGroup && (
        <MembershipModal group={memberGroup} leases={leases} onClose={() => setMemberGroup(null)} onSaved={loadGroups} />
      )}
    </div>
  )
}

// ─── Placeholder Tab ─────────────────────────────────────────────────────────

function PlaceholderTab({ title, description }: { title: string; description: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e8eaed',
      borderRadius: 10, padding: 56, textAlign: 'center',
    }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#9ca3af', maxWidth: 360, margin: '0 auto' }}>{description}</div>
    </div>
  )
}

// ─── Empty State Tab ─────────────────────────────────────────────────────────

function EmptyTab({ title, cta }: { title: string; cta?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid #e8eaed',
      borderRadius: 10, padding: 56, textAlign: 'center',
    }}>
      <div style={{ fontSize: 13, color: '#9ca3af' }}>
        {title}{cta && <><br /><span style={{ color: '#4f46e5' }}>{cta}</span></>}
      </div>
    </div>
  )
}

// ─── Opsætning Tab ───────────────────────────────────────────────────────────

function OpsaetningTab({ property }: { property: PropertyDetail }) {
  const navigate = useNavigate()
  const [status, setStatus] = useState(property.status)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiFetch(`/api/v1/properties/${property.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      })
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // ignore for now
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Ejendommens indstillinger
        </span>
      </div>
      <div style={{ padding: '20px 24px' }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, fontWeight: 500, color: '#6b7280', display: 'block', marginBottom: 6 }}>
            Status
          </label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value)}
            style={{
              height: 40, padding: '0 12px', borderRadius: 7,
              border: '1px solid #e8eaed', fontSize: 14,
              color: '#111827', background: '#fff', fontFamily: 'inherit',
            }}
          >
            <option value="active">Aktiv</option>
            <option value="inactive">Inaktiv</option>
          </select>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleSave} isLoading={saving}>
            {saved ? 'Gemt ✓' : 'Gem ændringer'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/ejendomme')}>
            Tilbage
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Page shell ──────────────────────────────────────────────────────────────

export function PropertyDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [property, setProperty] = useState<PropertyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('generelt')

  useEffect(() => {
    if (!id) return
    apiFetch<PropertyDetail>(`/api/v1/properties/${id}`)
      .then(setProperty)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Henter…</div>
      </AppLayout>
    )
  }

  if (notFound || !property) {
    return (
      <AppLayout>
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>Ejendommen blev ikke fundet.</p>
          <Link to="/ejendomme"><Button variant="secondary">← Tilbage</Button></Link>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Property header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{
            fontFamily: 'monospace', fontSize: 13, fontWeight: 700,
            color: '#4f46e5', background: '#eff6ff',
            padding: '3px 8px', borderRadius: 5,
          }}>
            {property.serial_label}
          </span>
          <h1 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0 }}>
            {property.name ?? property.address}
          </h1>
          <Chip variant={property.status === 'active' ? 'green' : 'gray'}>
            {statusLabel(property.status)}
          </Chip>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button size="sm" onClick={() => navigate(`/lejemaal/opret?property_id=${property.id}`)}>
            + Opret lejemål
          </Button>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e8eaed',
        marginBottom: 20,
        overflowX: 'auto',
        gap: 0,
      }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '11px 16px',
              fontSize: 13,
              fontWeight: activeTab === tab.key ? 600 : 400,
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              background: 'none',
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: activeTab === tab.key ? '2px solid #4f46e5' : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'color 0.12s, border-color 0.12s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'generelt' && <GenereltTab property={property} />}
      {activeTab === 'lejemaal' && <LejemaalTab property={property} />}
      {activeTab === 'faellesarealer' && (
        <EmptyTab title="Ingen fællesarealer registreret endnu." cta="Tilføj fællesareal kommende funktion" />
      )}
      {activeTab === 'forbrug' && <ForbrugTab property={property} />}
      {activeTab === 'kontokort' && (
        <PlaceholderTab
          title="Kontokort"
          description="Økonomi og kontokort for ejendommen vil blive tilgængeligt når faktureringslaget er klar."
        />
      )}
      {activeTab === 'billeder' && <BillederTab basePath={`/api/v1/properties/${property.id}`} />}
      {activeTab === 'dokumenter' && <DokumenterTab basePath={`/api/v1/properties/${property.id}`} />}
      {activeTab === 'kontakter' && <KontakterTab basePath={`/api/v1/properties/${property.id}`} />}
      {activeTab === 'opgaver' && <OpgaverTab basePath={`/api/v1/properties/${property.id}`} />}
      {activeTab === 'opsaetning' && <OpsaetningTab property={property} />}
    </AppLayout>
  )
}
